"use client";

import * as React from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { motion } from "framer-motion";
import { Eye } from "lucide-react";
import Image from "@/components/Image";

type ImageInfo = {
  id: number;
  src: string;
  alt: string;
  hint: string;
};

type Location = {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  hint: string;
  images: ImageInfo[];
};

export function CityLocations({ locations }: { locations: Location[] }) {
  const cardVariants = {
    initial: { opacity: 0, y: 50, scale: 0.9 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
  };

  const cardRef = React.useRef<HTMLDivElement>(null);
  
  const useCard3D = (ref: React.RefObject<HTMLDivElement>) => {
    const [rotateX, setRotateX] = React.useState(0);
    const [rotateY, setRotateY] = React.useState(0);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const xPct = mouseX / width - 0.5;
      const yPct = mouseY / height - 0.5;
      setRotateX(-yPct * 10);
      setRotateY(xPct * 10);
    };

    const handleMouseLeave = () => {
      setRotateX(0);
      setRotateY(0);
    };

    return { rotateX, rotateY, handleMouseMove, handleMouseLeave };
  };

  const CardWith3DEffect = ({ location, index }: { location: Location, index: number }) => {
    const cardRef = React.useRef<HTMLDivElement>(null);
    const { rotateX, rotateY, handleMouseMove, handleMouseLeave } = useCard3D(cardRef);

    return (
       <motion.div
        ref={cardRef}
        key={location.id}
        variants={cardVariants}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        style={{
          transformStyle: "preserve-3d",
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <Card className="overflow-hidden h-full flex flex-col transition-all duration-200 ease-out transform-gpu" style={{transformStyle: "preserve-3d"}}>
          <CardHeader className="p-0">
            <div className="relative aspect-video" style={{transform: "translateZ(20px)"}}>
              <Image
                src={location.thumbnail}
                alt={`Imagen de ${location.name}`}
                className="object-cover w-full h-full"
                data-ai-hint={location.hint}
              />
            </div>
          </CardHeader>
          <CardContent className="p-4 flex-grow" style={{transform: "translateZ(10px)"}}>
            <CardTitle className="text-xl mb-2">{location.name}</CardTitle>
            <p className="text-sm text-muted-foreground line-clamp-3">{location.description}</p>

          </CardContent>
          <CardFooter className="p-4 pt-0 mt-auto" style={{transform: "translateZ(5px)"}}>
             <LocationDialog location={location} />
          </CardFooter>
        </Card>
      </motion.div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 perspective-1000">
      {locations.map((location, index) => (
       <CardWith3DEffect key={location.id} location={location} index={index} />
      ))}
    </div>
  );
}


function LocationDialog({ location }: { location: Location }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <Eye className="mr-2 h-4 w-4" />
          Ver Galería
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-3xl font-headline text-primary mb-4">{location.name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <Carousel className="w-full">
              <CarouselContent>
                {location.images.map((image) => (
                  <CarouselItem key={image.id}>
                    <div className="p-1">
                      <Card className="overflow-hidden">
                        <div className="relative aspect-video">
                          <Image
                            src={image.src}
                            alt={image.alt}
                            className="object-cover w-full h-full"
                            data-ai-hint={image.hint}
                          />
                        </div>
                      </Card>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="ml-12" />
              <CarouselNext className="mr-12" />
            </Carousel>
          </div>
          <div className="flex items-center">
            <p className="text-muted-foreground">{location.description}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
